// Mappers for converting between DB (snake_case) and Store (camelCase) formats
import type { Quote, OverallAssessment } from '@api/types'
import type { QuoteDetails, SelectedProduct, PartnershipDetails, CurrencyType, PlanType, PaymentFrequency, QuoteCalculations } from '../types'
import { HOSTED_ONESDK_ANNUAL_FEE } from '../data'

interface QuoteStoreState {
  details: QuoteDetails
  selectedProducts: SelectedProduct[]
  partnership?: PartnershipDetails
}

// Validate currency string against allowed values
function toCurrencyType(value: string): CurrencyType | '' {
  const valid: CurrencyType[] = ['AUD', 'USD', 'NZD', 'EUR', 'CAD']
  return valid.includes(value as CurrencyType) ? (value as CurrencyType) : ''
}

function toPlanType(value: string): PlanType | '' {
  const valid: PlanType[] = ['Basic', 'Professional', 'Enterprise']
  return valid.includes(value as PlanType) ? (value as PlanType) : ''
}

function toPaymentFrequency(value: string): PaymentFrequency | '' {
  const valid: PaymentFrequency[] = ['Annual', 'Quarterly', 'Monthly']
  return valid.includes(value as PaymentFrequency) ? (value as PaymentFrequency) : ''
}

/**
 * Maps a DB quote record (snake_case) to the frontend store state (camelCase).
 * The `customerName` comes from the deal, not the quote, so it's passed separately.
 */
export function mapDbQuoteToStoreState(
  dbQuote: Quote,
  customerName?: string
): QuoteStoreState {
  const details: QuoteDetails = {
    customerName: customerName || '',
    country: (dbQuote as unknown as Record<string, unknown>).country as string || '',
    customerPlan: toPlanType(dbQuote.customer_plan || ''),
    preparedBy: dbQuote.prepared_by || '',
    startDate: dbQuote.start_date || '',
    displayCurrency: toCurrencyType(dbQuote.currency || ''),
    contractTermMonths: dbQuote.contract_term_months || 0,
    paymentFrequency: toPaymentFrequency(dbQuote.payment_frequency || ''),
    platformFees: dbQuote.platform_fees || 0,
    implementationFee: dbQuote.implementation_fee || 0,
    implementationFeeFromScope: !!dbQuote.implementation_fee_from_scope,
    implementationScopeHours: dbQuote.implementation_scope_hours ?? null,
    implementationScopeAppliedAt: dbQuote.implementation_scope_applied_at ?? null,
    scopeSourceDealId: (dbQuote as unknown as Record<string, unknown>).scope_source_deal_id as number | null ?? null,
    minimumCommitment: dbQuote.minimum_commitment || 0,
    hourlyRate: dbQuote.hourly_rate || 0,
    portalSeats: dbQuote.portal_seats || 0,
    supportLevel: typeof dbQuote.support_level === 'string'
      ? parseFloat(dbQuote.support_level) || 0
      : (dbQuote.support_level as unknown as number) || 0,
    implementationSupport: typeof dbQuote.implementation_support === 'string'
      ? parseFloat(dbQuote.implementation_support) || 0
      : (dbQuote.implementation_support as unknown as number) || 0,
    // DB stores a boolean flag; the store holds the dollar amount that feeds
    // calculateQuote's annualPlatformFees, so restore the full annual fee.
    hostedOneSDK: dbQuote.hosted_onesdk ? HOSTED_ONESDK_ANNUAL_FEE : 0,
    partnerCommission: dbQuote.partner_commission || 0,
    childAccounts: 0,
    autoRenew: true,
  }

  let selectedProducts: SelectedProduct[] = []
  try {
    selectedProducts = JSON.parse(dbQuote.products_json || '[]')
  } catch {
    console.error('Failed to parse products_json:', dbQuote.products_json)
  }

  return {
    details,
    selectedProducts,
  }
}

/**
 * Maps frontend store state (camelCase) to a DB-compatible quote object (snake_case).
 * Used when saving a quote to the backend.
 */
export function mapStoreStateToDbQuote(
  state: { details: QuoteDetails; selectedProducts: SelectedProduct[]; partnership?: PartnershipDetails; calculations?: QuoteCalculations | null }
): Partial<Quote> {
  const { details, selectedProducts, calculations } = state

  return {
    customer_plan: (details.customerPlan || 'Basic') as Quote['customer_plan'],
    prepared_by: details.preparedBy || null,
    start_date: details.startDate || null,
    currency: details.displayCurrency || 'AUD',
    contract_term_months: details.contractTermMonths || 24,
    payment_frequency: (details.paymentFrequency || 'Annual') as Quote['payment_frequency'],
    platform_fees: details.platformFees || 0,
    implementation_fee: details.implementationFee || 0,
    implementation_fee_from_scope: details.implementationFeeFromScope ? 1 : 0,
    implementation_scope_hours: details.implementationScopeHours,
    implementation_scope_applied_at: details.implementationScopeAppliedAt,
    scope_source_deal_id: details.scopeSourceDealId,
    minimum_commitment: details.minimumCommitment || 0,
    hourly_rate: details.hourlyRate || 0,
    portal_seats: details.portalSeats || 0,
    support_level: String(details.supportLevel || 0),
    implementation_support: String(details.implementationSupport || 0),
    hosted_onesdk: details.hostedOneSDK > 0,
    partner_commission: details.partnerCommission || 0,
    products_json: JSON.stringify(selectedProducts),
    // Persist the derived metrics (revenue, margin, rating) so server-side views — the
    // home "In Review"/"Approved" queues and the review Slack message — can read them
    // without recomputing. Only written when the caller supplies calculations; otherwise
    // the server's COALESCE leaves the existing values untouched.
    ...(calculations
      ? {
          mrr: calculations.mrr,
          arr: calculations.arr,
          tcv: calculations.tcv,
          gross_profit: calculations.grossProfit,
          gross_profit_margin: calculations.grossProfitMargin,
          ltv_cac_ratio: calculations.ltvCacRatio,
          payback_months: calculations.paybackMonths,
          overall_assessment: (calculations.overallRating?.rating?.toLowerCase() ?? null) as OverallAssessment | null,
        }
      : {}),
  }
}
